<script lang="ts">
    import { Component, Prop, mixins } from 'vue-facing-decorator';
    import ButtonComponent from "@/components/atoms/button/Button.vue";
    import IconComponent from "@/components/atoms/Icon.vue";
    import TicketTypeComponent from "@/components/ticket-selection/best-seat-selection/TicketType.vue";
    import { PriceLevel, PromotionHold, TicketType} from "webshop-api";
    import {
        CurrencyHandler,
        IconName,
        TranslationKey,
        PerformanceQASelectors,
        AccessibilityHandler
    } from 'webshop-modules';
    import { useBestSeatStore } from "@/domain/ticket-selection/application/BestSeat.store";
    import { usePromotionStore } from "@/domain/ticket-selection";
    import { PromotionHoldState, TicketTypeState } from "@/domain/ticket-selection/entities/ticket-selection";
    import { TranslationPlugin } from "@/plugins/translation.plugin";
    import Velocity from 'velocity-animate';
    import { PriceLevelState } from "@/domain/ticket-selection/entities/ticket-selection/PriceLevelState.entity";
    import { BreakpointHandler } from "@/mixins/BreakpointHandler.mixin";
    import TicketFeeInfo from "@/components/atoms/ticket-fee/TicketFeeInfo.vue";

    @Component({
        name: 'PriceLevelComponent',
        components: {
            TicketFeeInfo,
            ButtonComponent,
            IconComponent,
            TicketTypeComponent,
        }
    })
    export default class PriceLevelComponent extends mixins(BreakpointHandler) {

        @Prop() public data!: PriceLevel;
        @Prop() public eventName!: string;

        public readonly IconName: typeof IconName = IconName;
        public readonly Translation: typeof TranslationKey = TranslationKey;
        public readonly bestSeatStore = useBestSeatStore();
        public readonly promotionStore = usePromotionStore();
        public readonly CurrencyHandler = new CurrencyHandler(
            this.bestSeatStore.currency ?? '',
            this.bestSeatStore.currencyOptions?.currency ?? '',
            this.bestSeatStore.currencyOptions?.conversionRate ?? '1',
            this.bestSeatStore.currencyOptions?.showDoubleCurrency ?? false
        );
        public readonly PerformanceQASelectors: typeof PerformanceQASelectors = PerformanceQASelectors;

        public shouldNotHideSubmit = false;
        public submitLabel = '';

        private isDesktopBreakpoint = false;

        public get priceLevelState(): PriceLevelState | undefined {
            return this.bestSeatStore.priceLevels.find(
                (priceLevel: PriceLevelState) => priceLevel.priceLevelId === this.data.priceLevelId
            );
        }

        public get promotionState(): PromotionHoldState | undefined {
            const priceLevelState = this.priceLevelState;

            if (priceLevelState === undefined) {
                return undefined;
            }

            return priceLevelState.promotionHolds!.find((promotion: PromotionHoldState) => promotion.active);
        }

        public get ticketTypes(): TicketType[] | undefined {
            if (!this.data.promotionHolds || !this.promotionState) {
                return undefined;
            }

            const promotion = this.data.promotionHolds
                .find((promotion: PromotionHold) => promotion.id === this.promotionState!.id);

            return promotion !== undefined ? promotion.holds![0].ticketTypes : undefined;
        }

        public get selectionAmount(): number {
            if (!this.promotionState) {
                return 0;
            }

            return this.promotionState.holds[0].ticketTypes.reduce((acc: number, cur: TicketTypeState) => {
                    return acc + cur.amount;
                },0);
        }

        public get isAvailable(): boolean {
            if (!this.promotionState || !this.promotionState.holds) {
                return false;
            }

            return this.promotionState.holds[0].maxTickets > 0
                && this.data.status === '1';
        }

        public get submitButtonLabel(): string {
            return this.isDesktopBreakpoint
                ? `${this.CurrencyHandler.displayPrimaryCurrency(this.ticketsPrice ? this.ticketsPrice : 0)}`
                : TranslationPlugin.decorateWithParams(this.submitLabel, {
                    key: this.Translation.Ticket_selection_ticket_count_submit,
                    keyValues: JSON.stringify({
                        'amount': this.selectionAmount,
                        'price': this.CurrencyHandler.displayPrimaryCurrency(this.ticketsPrice ? this.ticketsPrice : 0)
                    }),
                    identifierStart: '{',
                    identifierEnd: '}'
                });
        }

        public get minPrice(): number {
            if (!this.ticketTypes) {
                return 0;
            }

            const price = this.ticketTypes.reduce((defaultType: TicketType, ticketType: TicketType) => {
                return !ticketType.number < !defaultType.number ? ticketType : defaultType;
            }).price!;

            return price;
        }

        public get ticketsPrice(): number {
            if (!this.promotionState || !this.promotionState.holds || !this.promotionState.holds[0].ticketTypes || !this.ticketTypes) {
                return 0;
            }

            const price = this.promotionState.holds[0].ticketTypes.reduce(
                (pre: number, cur: TicketTypeState): number => {
                    const data = this.ticketTypes!.find(type => type.ticketTypeID === cur.ticketTypeID);

                    if (!data || !data.price) {
                        return pre;
                    }

                    return pre + (cur.amount * (data.price! * 100)) / 100
                }, 0);

            return price;
        }

        public getPriceListOfSelectedTickets(): number[] {
            if (!this.promotionState || !this.promotionState.holds || !this.promotionState.holds[0].ticketTypes || !this.ticketTypes) {
                return [];
            }

            const priceList = this.promotionState.holds[0].ticketTypes.reduce(
                (pre: number[], cur: TicketTypeState) => {
                    const data = this.ticketTypes!.find(type => type.ticketTypeID === cur.ticketTypeID);

                    if (!data || !data.price) {
                        return pre;
                    }

                    for (let i = 0; i < cur.amount; i++) {
                        pre.push(data.price);
                    }

                    return pre;
                }, []);

            return priceList;
        }

        public canDisplayTicketType(index: number): boolean {
            if (!this.priceLevelState) {
                return false;
            }

            return this.priceLevelState.open ? true : index < 1;
        }

        public canDisplaySubmitButtonFor(priceLevelState: PriceLevelState | undefined): boolean {
            if (!priceLevelState) {
                return false;
            }

            return priceLevelState && (priceLevelState.canSubmit || priceLevelState.active || this.shouldNotHideSubmit)
        }

        public handleKeyPress(event: KeyboardEvent, priceLevelState: PriceLevelState | undefined) {
            if (!priceLevelState) {
                return false;
            }

            AccessibilityHandler.handleActionWithCallback(event, () => {
                this.bestSeatStore.togglePriceLevelDisplay(priceLevelState);
            })
        }

        /** Breakpoint change handler init */
        protected handleBreakpointInitialization() {
            this.updateBreakpointState();
        }

        /** Breakpoint change handler method */
        protected handleDesktopChange(event: MediaQueryListEvent) {
            this.updateBreakpointState();
        }

        /** Animation methods */
        protected beforeEnter(element: HTMLElement) {
            element.style.opacity = '0';
            element.style.height = '0';
        }

        protected enter(element: HTMLElement, done: any) {
            const delay = Number.parseInt(element.dataset.index as string) * 150;
            const css = getComputedStyle(element);
            const padding = Number.parseInt(css.paddingTop!.slice(0, css.paddingTop!.length - 2)) / 16;

            let height = 0;
            for (let i = 0; i < element.children.length; i++) {
                const item = element.children.item(i);
                const css = getComputedStyle(item!);
                let increase = Number.parseInt(css.height!.slice(0, css.height!.length - 2));

                // in iOS Safari item height is 0 even though children have height > 0
                if (increase === 0 && item && item.children) {
                    for (let i = 0; i < item.children.length; i++) {
                        const child = item.children.item(i);
                        const css = getComputedStyle(child!);
                        const increment = Number.parseInt(css.height!.slice(0, css.height!.length - 2));
                        increase += increment;
                    }
                }

                height = height === increase ? height : height + increase;
            }

            setTimeout(() => {
                Velocity.animate(
                    element,
                    { opacity: 1, height: `${(height / 16) + padding}rem` },
                    { complete: () => { element.style.height = 'auto'; done(); }}
                )
            }, delay);
        }

        protected leave(element: HTMLElement, done: any) {
            const html = document.querySelector('html') as HTMLElement;
            const delay = Number.parseInt(element.dataset.index as string) * 100;
            const targetOffset = element.parentElement!.offsetTop;

            setTimeout(() => {
                Velocity.animate(
                    element,
                    {opacity: 0, height: 0},
                    { complete: () => { element.style.height = 'auto'; done(); }}
                );
                html.scrollTop = html.scrollTop > targetOffset ? targetOffset : html.scrollTop;
            }, delay);
        }

        /** Created lifecycle handler method */
        protected async created() {
            this.submitLabel = await this.$translationService
                .getTranslationFor(this.Translation.Ticket_selection_ticket_count_submit);
        }

        private updateBreakpointState(): void {
            this.isDesktopBreakpoint = this.desktopBreakpoint.matches;
            this.shouldNotHideSubmit = this.desktopBreakpoint.matches;
        }

    }
</script>

<template>
    <div v-if="ticketTypes && priceLevelState"
         :data-qa="PerformanceQASelectors.PRICE_LEVEL"
         class="m-priceLevel"
         v-bind:class="{
            '-is-disabled' : !isAvailable,
            '-is-open': priceLevelState && priceLevelState.open && isAvailable }">
        <div class="m-priceLevel__section -verticallyAligned">
            <h4 class="m-priceLevel__title">{{ priceLevelState.text }}</h4>
            <p v-if="priceLevelState.description" class="m-priceLevel__description">{{ priceLevelState.description }}</p>
        </div>

        <template v-if="isAvailable">
            <div :data-qa="'price-level'" class="m-priceLevel__section">
                <transition-group name="staggered-fade"
                                  tag="div"
                                  v-bind:css="false"
                                  v-on:before-enter="beforeEnter"
                                  v-on:enter="enter"
                                  v-on:leave="leave">
                    <TicketTypeComponent v-for="(ticketType, index) of ticketTypes"
                                         v-show="canDisplayTicketType(index)"
                                         :key="ticketType.ticketTypeID"
                                         :data="ticketType"
                                         :connected-price-level="data.priceLevelId" />
                </transition-group>

                <div class="m-priceLevelIndicator" v-if="ticketTypes.length > 1 && priceLevelState && !priceLevelState.alwaysOpen">
                    <div v-if="!priceLevelState.open"
                         class="m-priceLevelIndicator__content"
                         v-on:click.stop="bestSeatStore.togglePriceLevelDisplay(priceLevelState)"
                         @keydown="handleKeyPress($event, priceLevelState)"
                         tabindex="0">
                        <IconComponent :icon-id="IconName.Arrow"/>
                        <span v-translate="{ key: Translation.Ticket_selection_show_all_prices_label }">Show all prices</span>
                        <IconComponent :icon-id="IconName.Arrow"/>
                    </div>

                    <div v-if="priceLevelState.open"
                         v-on:click.stop="bestSeatStore.togglePriceLevelDisplay(priceLevelState)"
                         class="m-priceLevelIndicator__content -is-open">
                        <IconComponent :icon-id="IconName.Arrow"/>
                    </div>
                </div>
            </div>

            <div class="m-priceLevel__section">
                <div v-if="canDisplaySubmitButtonFor(priceLevelState)"
                     :data-qa="'price-level-submit'"
                     :class="CurrencyHandler.isSecondaryCurrencyDisplayed ? '-marginTop' : ''"
                     class="m-priceLevel__action"
                     >
                    <ButtonComponent
                            :active="priceLevelState.active && priceLevelState.canSubmit"
                            :icon-name="IconName.Cart"
                            :label="submitButtonLabel"
                            :secondary-label="CurrencyHandler.displaySecondaryCurrency(ticketsPrice)"
                            v-on:submit="bestSeatStore.submitSelection({
                                state: priceLevelState,
                                promotion: promotionStore.activePromotion,
                                promotionCode: promotionStore.promotionCode,
                                eventName,
                            })"
                            :qa-selector="PerformanceQASelectors.PRICE_LEVEL_SUBMIT"
                    />
                    <TicketFeeInfo :fee-list="bestSeatStore.fees"
                                   :ticket-price-list="getPriceListOfSelectedTickets()"
                                   :currency-handler="CurrencyHandler"
                                   :has-opacity="!priceLevelState.active || !priceLevelState.canSubmit">
                    </TicketFeeInfo>
                </div>
            </div>
        </template>

        <div class="m-priceLevel__section" v-else>
            <div class="m-priceLevel__status" v-if="ticketTypes && ticketTypes.length">
                <p v-translate="{
                    key: Translation.Ticket_selection_start_price_label,
                    keyValues: JSON.stringify({ 'min-price': CurrencyHandler.displayPrimaryCurrency(minPrice) }),
                    identifierStart: '{',
                    identifierEnd: '}',
                }">Prices starting at:</p>
                <div :class="CurrencyHandler.isSecondaryCurrencyDisplayed ? '' : '-centered'" class="m-priceLevel__price">
                    <span class="m-priceLevel__pricePrimary">
                        {{
                            CurrencyHandler.displayPrimaryCurrency(minPrice)
                        }}</span>
                    <small v-if="CurrencyHandler.isSecondaryCurrencyDisplayed" class="m-priceLevel__priceSecondary">
                        {{
                            CurrencyHandler.displaySecondaryCurrency(minPrice)
                        }}</small>
                </div>
                <p><b>{{ data.statusDescription }}</b></p>
            </div>
        </div>
    </div>
</template>
